import React, {useEffect, useMemo, useRef, useState} from "react";
import {DateObject} from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import gregorian from "react-date-object/calendars/gregorian";
import persian_fa from "react-date-object/locales/persian_fa";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {createPortal} from "react-dom";
import Icon from "./icon";
import "./zofino-calendar.css";

const VIEW_MONTH = "jalaliMonth";
const VIEW_WEEK = "dayGridWeek";

function persianDate(value) {
    return new DateObject(value).setCalendar(persian).setLocale(persian_fa);
}

function getRangeLabel(viewType, start, end, currentDate) {
    if (!start || !end) return "";
    const rangeStart = persianDate(start);
    const rangeEndDate = new Date(end);
    rangeEndDate.setDate(rangeEndDate.getDate() - 1);
    const rangeEnd = persianDate(rangeEndDate);

    if (viewType === VIEW_MONTH) {
        const activeMonth = persianDate(currentDate ?? start);
        return `${activeMonth.month.name} ${activeMonth.year}`;
    }
    if (rangeStart.year === rangeEnd.year && rangeStart.month.number === rangeEnd.month.number) {
        return `${rangeStart.day} – ${rangeEnd.day} ${rangeStart.month.name} ${rangeStart.year}`;
    }
    if (rangeStart.year === rangeEnd.year) {
        return `${rangeStart.day} ${rangeStart.month.name} – ${rangeEnd.day} ${rangeEnd.month.name} ${rangeStart.year}`;
    }
    return `${rangeStart.day} ${rangeStart.month.name} ${rangeStart.year} – ${rangeEnd.day} ${rangeEnd.month.name} ${rangeEnd.year}`;
}

function isCurrentCalendarPeriod(viewType, currentDate, start, end) {
    const today = new Date();
    if (viewType === VIEW_MONTH && currentDate) {
        const activeMonth = persianDate(currentDate);
        const currentMonth = persianDate(today);
        return activeMonth.year === currentMonth.year && activeMonth.month.number === currentMonth.month.number;
    }
    return Boolean(start && end && today >= start && today < end);
}

function getEventTone(task) {
    if (task?.taskStatus === "done") return "done";
    const status = String(task?.healthStatus ?? task?.health_status ?? task?.status ?? "").toLowerCase();
    if (["off-track", "off_track", "danger", "blocked"].includes(status)) return "danger";
    if (["at-risk", "at_risk", "warning"].includes(status)) return "warning";
    if (["healthy", "on-track", "on_track"].includes(status)) return "healthy";
    const tones = ["teal", "blue", "purple", "orange"];
    const key = String(task?.task_type?.id ?? task?.documentId ?? task?.id ?? task?.title ?? "");
    return tones[[...key].reduce((sum, char) => sum + char.charCodeAt(0), 0) % tones.length];
}

function jalaliMonthRange(currentDate) {
    const base = persianDate(currentDate);
    const startPersian = new DateObject({
        year: base.year,
        month: base.month.number,
        day: 1,
        calendar: persian,
    });
    const endPersian = new DateObject(startPersian).add(1, "month");
    const start = startPersian.convert(gregorian).toDate();
    const end = endPersian.convert(gregorian).toDate();
    const padStart = new Date(start);
    padStart.setDate(start.getDate() - ((start.getDay() - 6 + 7) % 7));
    const padEnd = new Date(end);
    padEnd.setDate(end.getDate() + ((5 - end.getDay() + 7) % 7) + 1);
    return {start: padStart, end: padEnd};
}

function ZofinoCalendar({events = [], onAddEventClick, onEventClick, onEventResize, onEventDropped, eventTemplate, toolbarTargetId, primaryToolbarTargetId, secondaryToolbarTargetId, navigateToDate, taskToolbar = false, onRangeChange}) {
    const calendarRef = useRef(null);
    const [viewType, setViewType] = useState(VIEW_MONTH);
    const [selectedView, setSelectedView] = useState(null);
    const [visibleRange, setVisibleRange] = useState({start: null, end: null});
    const [currentDate, setCurrentDate] = useState(null);
    const [todayActive, setTodayActive] = useState(true);
    const [toolbarTarget, setToolbarTarget] = useState(null);
    const [primaryToolbarTarget, setPrimaryToolbarTarget] = useState(null);
    const [secondaryToolbarTarget, setSecondaryToolbarTarget] = useState(null);
    useEffect(() => {
        setToolbarTarget(toolbarTargetId ? document.getElementById(toolbarTargetId) : null);
    }, [toolbarTargetId]);
    useEffect(() => {
        setPrimaryToolbarTarget(primaryToolbarTargetId ? document.getElementById(primaryToolbarTargetId) : null);
        setSecondaryToolbarTarget(secondaryToolbarTargetId ? document.getElementById(secondaryToolbarTargetId) : null);
    }, [primaryToolbarTargetId, secondaryToolbarTargetId]);
    useEffect(() => {
        if (navigateToDate) calendarRef.current?.getApi()?.gotoDate(navigateToDate);
    }, [navigateToDate]);
    const rangeLabel = useMemo(
        () => getRangeLabel(viewType, visibleRange.start, visibleRange.end, currentDate),
        [viewType, visibleRange, currentDate],
    );

    const calendarApi = () => calendarRef.current?.getApi();
    const navigate = (direction) => direction === "next" ? calendarApi()?.next() : calendarApi()?.prev();
    const changeView = (nextView) => {
        if (selectedView === nextView) {
            setSelectedView(null);
            return;
        }
        setSelectedView(nextView);
        setViewType(nextView);
        calendarApi()?.changeView(nextView);
    };
    const primaryToolbar = (
        <div className="zofino-calendar__toolbar zofino-calendar__toolbar--primary">
            <div className="zofino-calendar__view-toggle" role="group" aria-label="نوع نمایش تقویم">
                <button type="button" aria-pressed={selectedView === VIEW_MONTH} className={selectedView === VIEW_MONTH ? "is-active" : ""} onClick={() => changeView(VIEW_MONTH)}>ماهانه</button>
                <button type="button" aria-pressed={selectedView === VIEW_WEEK} className={selectedView === VIEW_WEEK ? "is-active" : ""} onClick={() => changeView(VIEW_WEEK)}>هفتگی</button>
            </div>
            {(taskToolbar || primaryToolbarTargetId) && <button type="button" aria-pressed={todayActive} className={`zofino-calendar__today ${todayActive ? "is-active" : ""}`} onClick={() => calendarApi()?.today()}>امروز</button>}
            <div className="zofino-calendar__navigation">
                <button type="button" className="zofino-calendar__arrow" aria-label="بازه قبلی" onClick={() => navigate("prev")}>
                    <Icon icon="mi mi:chevron_right" size={19}/>
                </button>
                <strong title={rangeLabel}>{rangeLabel}</strong>
                <button type="button" className="zofino-calendar__arrow" aria-label="بازه بعدی" onClick={() => navigate("next")}>
                    <Icon icon="mi mi:chevron_left" size={19}/>
                </button>
            </div>
        </div>
    );
    const secondaryToolbar = (
        <div className="zofino-calendar__toolbar zofino-calendar__toolbar--secondary">
            {!taskToolbar && <button type="button" aria-pressed={todayActive} className={`zofino-calendar__today ${todayActive ? "is-active" : ""}`} onClick={() => calendarApi()?.today()}>امروز</button>}
        </div>
    );
    const toolbar = <div className="zofino-calendar__toolbar">{secondaryToolbar}{primaryToolbar}</div>;
    return (
        <section className="zofino-calendar h-full min-h-0 flex flex-column" dir="rtl">
            {primaryToolbarTarget ? (
                <>
                    {createPortal(primaryToolbar, primaryToolbarTarget)}
                    {secondaryToolbarTarget && createPortal(secondaryToolbar, secondaryToolbarTarget)}
                </>
            ) : toolbarTarget ? createPortal(toolbar, toolbarTarget) : toolbar}

            <div className="zofino-calendar__body flex-1 min-h-0">
                <FullCalendar
                    ref={calendarRef}
                    headerToolbar={false}
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView={VIEW_MONTH}
                    views={{[VIEW_MONTH]: {type: "dayGrid", visibleRange: jalaliMonthRange}}}
                    datesSet={(info) => {
                        const activeDate = info.view.calendar.getDate();
                        setViewType(info.view.type);
                        setVisibleRange({start: info.start, end: info.end});
                        onRangeChange?.(info.start, info.end);
                        setCurrentDate(activeDate);
                        setTodayActive(isCurrentCalendarPeriod(info.view.type, activeDate, info.start, info.end));
                    }}
                    height="100%"
                    firstDay={6}
                    direction="rtl"
                    locale="fa"
                    editable
                    eventStartEditable
                    eventDurationEditable
                    eventClick={onEventClick}
                    eventDrop={onEventDropped}
                    eventResize={onEventResize}
                    eventContent={eventTemplate}
                    eventClassNames={(arg) => {
                        const data = arg.event.extendedProps.data;
                        return ["zofino-event", `zofino-event--${getEventTone(data)}`, data?.projected ? "zofino-event--projected" : "", data?.occurrenceStatus === "skipped" ? "zofino-event--skipped" : ""].filter(Boolean);
                    }}
                    dayHeaderFormat={{weekday: "long"}}
                    dayCellClassNames={(arg) => [arg.isToday ? "zofino-day--today" : "", arg.isOther ? "zofino-day--other" : ""].filter(Boolean)}
                    dayCellContent={(arg) => (
                        <div className="zofino-calendar__day-head">
                            <span className="zofino-calendar__day-number">{persianDate(arg.date).day}</span>
                            {!arg.isOther && (
                                <button
                                    type="button"
                                    className="zofino-calendar__add"
                                    aria-label="افزودن رویداد"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onAddEventClick?.(arg.date);
                                    }}
                                >+</button>
                            )}
                        </div>
                    )}
                    moreLinkClick="popover"
                    events={events}
                />
            </div>
        </section>
    );
}

export default ZofinoCalendar;
